/**
 * Endpoints de **Mantenimiento → Sub-activos** (`/api/mantenimiento/sub-activos`).
 *
 * El listado acepta `page`, `per_page`, `search`, `activo_id`, `categoria_id` y
 * `estado`, y devuelve el consumo y el estado de cada sub-activo ya calculados.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type {
  MantenimientoPayload,
  SubActivoEstadoRespuesta,
  SubActivoGuardado,
  SubActivoPayload,
  SubActivosCatalogos,
  SubActivosFiltros,
  SubActivosRespuesta,
} from './sub-activos.types';

export const subActivosKeys = {
  todos: ['mantenimiento', 'sub-activos'] as const,
  lista: (filtros: SubActivosFiltros) => ['mantenimiento', 'sub-activos', filtros] as const,
  catalogos: ['mantenimiento', 'sub-activos', 'catalogos'] as const,
} as const;

/** Arma la cadena de consulta del listado. */
function cadenaDe(filtros: SubActivosFiltros): string {
  const parametros = new URLSearchParams();

  if (filtros.page) parametros.set('page', String(filtros.page));
  if (filtros.perPage) parametros.set('per_page', String(filtros.perPage));
  if (filtros.search) parametros.set('search', filtros.search);
  if (filtros.activoId) parametros.set('activo_id', String(filtros.activoId));
  if (filtros.categoriaId) parametros.set('categoria_id', String(filtros.categoriaId));
  if (filtros.estado) parametros.set('estado', filtros.estado);

  const cadena = parametros.toString();

  return cadena ? `?${cadena}` : '';
}

export const subActivosApi = {
  listar: (filtros: SubActivosFiltros = {}, signal?: AbortSignal): Promise<SubActivosRespuesta> =>
    apiClient.get<SubActivosRespuesta>(`/api/mantenimiento/sub-activos${cadenaDe(filtros)}`, {
      signal,
    }),
  catalogos: (signal?: AbortSignal): Promise<SubActivosCatalogos> =>
    apiClient.get<SubActivosCatalogos>('/api/mantenimiento/sub-activos/catalogos', { signal }),
  crear: (payload: SubActivoPayload): Promise<SubActivoGuardado> =>
    apiClient.post<SubActivoGuardado>('/api/mantenimiento/sub-activos', { body: payload }),
  actualizar: (id: number, payload: SubActivoPayload): Promise<SubActivoGuardado> =>
    apiClient.put<SubActivoGuardado>(`/api/mantenimiento/sub-activos/${id}`, { body: payload }),
  cambiarEstado: (id: number, activo: boolean): Promise<SubActivoEstadoRespuesta> =>
    apiClient.patch<SubActivoEstadoRespuesta>(`/api/mantenimiento/sub-activos/${id}/estado`, {
      body: { activo },
    }),
  registrarMantenimiento: (id: number, payload: MantenimientoPayload): Promise<SubActivoGuardado> =>
    apiClient.post<SubActivoGuardado>(`/api/mantenimiento/sub-activos/${id}/mantenimiento`, {
      body: payload,
    }),
};

/** Listado de sub-activos. */
export function useSubActivos(filtros: SubActivosFiltros = {}) {
  return useQuery({
    queryKey: subActivosKeys.lista(filtros),
    queryFn: ({ signal }) => subActivosApi.listar(filtros, signal),
  });
}

/** Activos y categorías para los filtros y el formulario. */
export function useSubActivosCatalogos() {
  return useQuery({
    queryKey: subActivosKeys.catalogos,
    queryFn: ({ signal }) => subActivosApi.catalogos(signal),
    staleTime: 5 * 60 * 1000,
  });
}

/** Alta de un sub-activo. */
export function useCrearSubActivo() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: subActivosApi.crear,
    onSuccess: () => {
      cliente.invalidateQueries({ queryKey: subActivosKeys.todos });
      // El listado de activos muestra el número de sub-activos.
      cliente.invalidateQueries({ queryKey: ['mantenimiento', 'activos'] });
    },
  });
}

/** Edición de un sub-activo. */
export function useActualizarSubActivo() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: SubActivoPayload }) =>
      subActivosApi.actualizar(id, payload),
    onSuccess: () => cliente.invalidateQueries({ queryKey: subActivosKeys.todos }),
  });
}

/** Marcar un sub-activo como activo o en desuso. */
export function useCambiarEstadoSubActivo() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ id, activo }: { id: number; activo: boolean }) =>
      subActivosApi.cambiarEstado(id, activo),
    onSuccess: () => cliente.invalidateQueries({ queryKey: subActivosKeys.todos }),
  });
}

/** Registrar un mantenimiento (reinicia el contador de consumo). */
export function useRegistrarMantenimiento() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: MantenimientoPayload }) =>
      subActivosApi.registrarMantenimiento(id, payload),
    onSuccess: () => cliente.invalidateQueries({ queryKey: subActivosKeys.todos }),
  });
}
